import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import { formatDate as formatDateUtil } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
  manager_name: string | null
  prepared_for: string | null
  matter: string | null
}

interface FinalEntry {
  id: number
  entry_type: number
  start_time: string | null
  timezone: string | null
  entry_text: string | null
  user_name: string | null
  also_names: string[]
  prepared: boolean
  has_photo: boolean
  has_video: boolean
}

interface FinalPhoto {
  id: number
  filename: string | null
  content_type: string | null
  document_date: string | null
  thumbnail_url: string | null
  width: number | null
  height: number | null
}

interface ReportGroup {
  report_id: number
  report_date: string | null
  team: string
  all_prepared: boolean
  entries: FinalEntry[]
  photos: FinalPhoto[]
}

interface Props {
  case_record: CaseRecord
  reports: ReportGroup[]
  all_prepared: boolean
  photo_count: number
  report_start: string | null
  report_end: string | null
  start_date: string | null
  end_date: string | null
}

function formatDate(dateStr: string | null): string {
  return formatDateUtil(dateStr, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  })
}

function formatTime(dateStr: string | null): string {
  if (!dateStr) return ""
  return new Date(dateStr).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
}

function EvidenceBadges({
  entry,
}: {
  entry: Pick<FinalEntry, "has_photo" | "has_video">
}) {
  return (
    <>
      {entry.has_photo && (
        <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs font-semibold text-green-700">
          P
        </span>
      )}
      {entry.has_video && (
        <span className="rounded bg-purple-100 px-1.5 py-0.5 text-xs font-semibold text-purple-700">
          V
        </span>
      )}
    </>
  )
}

const MEDIA_LEGEND = [
  { label: "P = Photo", className: "bg-green-100 text-green-700" },
  { label: "V = Video", className: "bg-purple-100 text-purple-700" },
  { label: "Prepared", className: "bg-hpi-light-green text-hpi-dark-green" },
]

export default function FinalPreviewByReport({
  case_record,
  reports,
  all_prepared,
  photo_count,
  report_start,
  report_end,
  start_date,
  end_date,
}: Props) {
  const [startDate, setStartDate] = useState(
    start_date ?? report_start?.slice(0, 10) ?? "",
  )
  const [endDate, setEndDate] = useState(
    end_date ?? report_end?.slice(0, 10) ?? "",
  )

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Reports", href: `/cases/${case_record.id}/reports` },
    {
      title: "Final Preview by Report",
      href: `/cases/${case_record.id}/final_report_preview_by_report`,
    },
  ]

  const basePath = `/cases/${case_record.id}`

  const handleUpdateDates = () => {
    const params = new URLSearchParams()
    if (startDate) params.set("start_date", startDate)
    if (endDate) params.set("end_date", endDate)
    const qs = params.toString()
    router.get(
      `${basePath}/final_report_preview_by_report${qs ? `?${qs}` : ""}`,
      {},
      { preserveState: false },
    )
  }

  const buildDateQueryString = () => {
    const params = new URLSearchParams()
    if (startDate) params.set("start_date", startDate)
    if (endDate) params.set("end_date", endDate)
    const qs = params.toString()
    return qs ? `?${qs}` : ""
  }

  const buildWordByDayUrl = () =>
    `${basePath}/report_downloads/final_report_by_day_word${buildDateQueryString()}`

  const buildWordByReportUrl = () =>
    `${basePath}/report_downloads/final_report_by_report_word${buildDateQueryString()}`

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Final Report Preview - ${case_record.case_number}`} />

      <div className="p-6">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Preview Final Report</h1>
          <Link
            href={`${basePath}/reports`}
            className="text-hpi-active-blue hover:underline"
          >
            Back to Reports
          </Link>
        </div>

        {/* Case info */}
        <div className="border-hpi-light-gray mb-4 rounded border bg-white p-4 text-center">
          {case_record.prepared_for && (
            <div className="text-sm">
              <span className="text-hpi-med-gray">Prepared For:</span>{" "}
              {case_record.prepared_for}
            </div>
          )}
          {case_record.matter && (
            <div className="text-sm">
              <span className="text-hpi-med-gray">In the Matter of:</span>{" "}
              {case_record.matter}
            </div>
          )}
          <div className="mt-2">
            {all_prepared ? (
              <span className="bg-hpi-light-green text-hpi-dark-green rounded px-2 py-1 text-sm">
                All entries prepared
              </span>
            ) : (
              <span className="rounded bg-red-100 px-2 py-1 text-sm text-red-700">
                Not all entries are prepared
              </span>
            )}
          </div>
        </div>

        {/* Date filter */}
        <div className="border-hpi-light-gray bg-hpi-pale-gray mb-4 flex flex-wrap items-end gap-4 rounded border p-4">
          <div className="text-sm font-medium">Dates</div>
          <div className="flex items-center gap-2">
            <label htmlFor="start_date" className="text-hpi-med-gray text-sm">
              Start
            </label>
            <input
              id="start_date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border-hpi-light-gray rounded border px-2 py-1 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="end_date" className="text-hpi-med-gray text-sm">
              End
            </label>
            <input
              id="end_date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border-hpi-light-gray rounded border px-2 py-1 text-sm"
            />
          </div>
          <button
            onClick={handleUpdateDates}
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-1 text-sm text-white"
          >
            Update
          </button>
        </div>

        {/* Action buttons */}
        <div className="border-hpi-light-gray mb-4 flex flex-wrap items-center gap-3 rounded border bg-white p-4">
          <Link
            href={`${basePath}/reports`}
            className="text-hpi-active-blue text-sm hover:underline"
          >
            Select Reports
          </Link>
          <a
            href={buildWordByDayUrl()}
            className="border-hpi-dark-blue text-hpi-dark-blue hover:bg-hpi-pale-blue rounded border px-4 py-1 text-sm"
          >
            Build Word Doc by Day
          </a>
          <a
            href={buildWordByReportUrl()}
            className="border-hpi-dark-blue text-hpi-dark-blue hover:bg-hpi-pale-blue rounded border px-4 py-1 text-sm"
          >
            Build Word Doc by Report
          </a>
        </div>

        {/* Photo count warning */}
        {photo_count > 200 && (
          <div className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-center text-sm text-red-700">
            There are <strong>{photo_count}</strong> photos. If the report will
            not load, choose a smaller date span and combine them in Word.
          </div>
        )}

        {/* Legend */}
        <div className="border-hpi-light-gray mb-4 flex flex-wrap items-center gap-2 rounded border bg-white p-3">
          <span className="text-hpi-med-gray text-xs font-medium">Legend:</span>
          {MEDIA_LEGEND.map((item) => (
            <span
              key={item.label}
              className={`rounded px-1.5 py-0.5 text-xs ${item.className}`}
            >
              {item.label}
            </span>
          ))}
        </div>

        {/* Report groups */}
        {reports.length > 0 ? (
          <div className="space-y-6">
            {reports.map((report) => (
              <div
                key={report.report_id}
                className="border-hpi-light-gray rounded border bg-white"
              >
                {/* Report header */}
                <div className="bg-hpi-pale-gray border-hpi-light-gray border-b px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">
                      {formatDate(report.report_date)}
                    </span>
                    {report.all_prepared ? (
                      <span className="bg-hpi-light-green text-hpi-dark-green rounded px-1.5 py-0.5 text-xs">
                        Prepared
                      </span>
                    ) : (
                      <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-700">
                        Not Prepared
                      </span>
                    )}
                  </div>
                  {report.team && (
                    <div className="text-hpi-med-gray text-sm">
                      Investigators: {report.team}
                    </div>
                  )}
                </div>

                {/* Entries */}
                <div className="divide-y divide-gray-100">
                  {report.entries.map((entry) => (
                    <div key={entry.id} className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="font-medium">
                          {formatTime(entry.start_time)}
                          {entry.timezone && (
                            <span className="ml-1 font-normal text-gray-500">
                              {entry.timezone}
                            </span>
                          )}
                        </span>
                        {entry.prepared && (
                          <span className="bg-hpi-light-green text-hpi-dark-green rounded px-1.5 py-0.5 text-xs">
                            Prepared
                          </span>
                        )}
                        <EvidenceBadges entry={entry} />
                        {entry.user_name && (
                          <span className="text-hpi-med-gray text-xs">
                            ({entry.user_name}
                            {entry.also_names.length > 0
                              ? `, ${entry.also_names.join(", ")}`
                              : ""}
                            )
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-sm whitespace-pre-wrap">
                        {entry.entry_text}
                      </div>
                    </div>
                  ))}

                  {/* Photos */}
                  {report.photos.length > 0 && (
                    <div className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        {report.photos.map((photo) => (
                          <div key={photo.id} className="inline-block">
                            {photo.thumbnail_url ? (
                              <img
                                src={photo.thumbnail_url}
                                alt={photo.filename ?? "Photo"}
                                className="h-24 w-auto rounded border border-gray-200 object-cover"
                              />
                            ) : (
                              <div className="flex h-24 w-24 items-center justify-center rounded border border-gray-200 bg-gray-50 text-xs text-gray-400">
                                No preview
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {report.entries.length === 0 &&
                    report.photos.length === 0 && (
                      <div className="text-hpi-med-gray px-4 py-3 text-sm">
                        No entries or photos for this report.
                      </div>
                    )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="border-hpi-light-gray rounded border bg-white p-6 text-center">
            <p className="text-hpi-med-gray">
              No reports are included in the final report. Use the report
              preparation pages to mark reports for inclusion.
            </p>
          </div>
        )}

        <div className="mt-6">
          <Link
            href={`${basePath}/reports`}
            className="text-hpi-active-blue hover:underline"
          >
            Back to Reports
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
